import { Component, OnInit, OnDestroy, inject, ViewEncapsulation } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';

// PrimeNG Imports
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

// Core Services & Config
import { PrimeNGConfigService } from './core/config/primeng.config';
import { OfflineSyncService } from './core/services/offline-sync.service';
import { ModulePreloaderService } from './core/services/module-preloader.service';
import { PlatformService } from './core/utils/platform.service';
import { NetworkService } from './core/services/network.service';

// Capacitor Imports
import { App } from '@capacitor/app';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SplashScreen } from '@capacitor/splash-screen';

@Component({
  selector: 'app-root',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [RouterOutlet, ToastModule, ConfirmDialogModule],
  providers: [MessageService],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Cimencam SFA';
  private readonly translate = inject(TranslateService);
  private readonly primeNGConfigService = inject(PrimeNGConfigService);
  private readonly messageService = inject(MessageService);
  private readonly offlineSyncService = inject(OfflineSyncService);
  private readonly modulePreloader = inject(ModulePreloaderService);
  private readonly router = inject(Router);
  private readonly platformService = inject(PlatformService);
  private readonly networkService = inject(NetworkService);

  private isOnline = true;
  private isSyncing = false;
  private preloadTriggered = false;

  async ngOnInit(): Promise<void> {

    try {
      // Initialize Capacitor if on native platform
      if (this.platformService.isNative) {
        await this.initializeCapacitor();
      }

      // Set default language
      this.translate.setDefaultLang('fr');

      // Try to use browser language or fallback to French
      const browserLang = this.translate.getBrowserLang();
      const langToUse = browserLang?.match(/en|fr/) ? browserLang : 'fr';
      this.translate.use(langToUse);

      // Initialize PrimeNG
      this.primeNGConfigService.initialize();

      await this.recoverInterruptedSync();

      // Setup network listeners for toasts
      this.setupNetworkListeners();

      // Précharger les modules critiques après navigation vers le dashboard
      this.setupModulePreloading();
    } catch (error) {
      console.error('❌ AppComponent initialization error:', error);
      // Ne pas bloquer l'app, continuer même en cas d'erreur
    }
  }

  ngOnDestroy(): void {
    // Nettoyer les écouteurs
    // Network listeners are handled by subscription
  }

  /**
   * Initialise Capacitor pour les plateformes natives
   */
  private async initializeCapacitor(): Promise<void> {
    try {
      // Configurer la StatusBar
      if (this.platformService.isIOS || this.platformService.isAndroid) {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#007bff' });
      }

      // Ajouter une classe sur le <body> pour activer les ajustements CSS spécifiques
      if (this.platformService.isAndroid) {
        document.body.classList.add('platform-android');
      } else if (this.platformService.isIOS) {
        document.body.classList.add('platform-ios');
      }

      // Cacher le splash screen après chargement
      await SplashScreen.hide();

      // Gérer le bouton retour Android
      if (this.platformService.isAndroid) {
        App.addListener('backButton', ({ canGoBack }) => {
          if (!canGoBack) {
            App.exitApp();
          } else {
            this.router.navigate(['/']);
          }
        });
      }


    } catch (error) {
      console.warn('⚠️ Capacitor initialization error:', error);
    }
  }

  /** Reprend les actions interrompues et démarre le planificateur de la file. */
  private async recoverInterruptedSync(): Promise<void> {
    try {
      await this.offlineSyncService.recoverStaleSyncingActions();
    } catch (error) {
      console.warn('⚠️ Sync recovery failed:', error);
    }
    this.offlineSyncService.startAutoSync();
  }

  private setupNetworkListeners(): void {
    // S'abonner aux changements d'état réseau via le service robuste
    this.networkService.isOnline$.subscribe(async (isOnline) => {
      const wasOffline = !this.isOnline;
      this.isOnline = isOnline;

      if (isOnline && wasOffline && !this.isSyncing) {
        // Toast : Connexion rétablie
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('COMMON.ONLINE'),
          detail: this.translate.instant('COMMON.ONLINE_MESSAGE'),
          life: 3000
        });

        // Attendre 500ms que la connexion se stabilise, puis sync automatique
        setTimeout(() => this.performAutoSync(), 500);
      } else if (!isOnline && !wasOffline) {
        // Toast : Connexion perdue
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('COMMON.OFFLINE'),
          detail: this.translate.instant('COMMON.OFFLINE_MESSAGE'),
          life: 5000
        });
      }
    });
  }


  /**
   * Configurer le préchargement des modules critiques
   * Déclenché après la première navigation vers /dashboard
   */
  private setupModulePreloading(): void {
    // Log toutes les navigations pour debug
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {

      }
    });

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(async (event: NavigationEnd) => {
        // Précharger uniquement après navigation vers le dashboard et une seule fois
        if (!this.preloadTriggered && event.url.startsWith('/dashboard')) {
          this.preloadTriggered = true;

          // Attendre un peu que la page dashboard se charge
          setTimeout(async () => {
            try {
              await this.modulePreloader.preloadCriticalModules();
            } catch (error) {
              console.warn('⚠️ Module preloading failed:', error);
            }
          }, 2000); // 2 secondes après l'affichage du dashboard
        }
      });
  }

  private async performAutoSync(): Promise<void> {
    if (this.isSyncing) {
      return;
    }

    try {
      this.isSyncing = true;

      // Synchroniser les actions hors ligne
      const result = await this.offlineSyncService.syncAll();

      // Recharger toutes les données depuis l'API
      await this.offlineSyncService.reloadAllData();

      // Toast de résultat
      if (result.failed === 0 && result.conflicts === 0 && result.success > 0) {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('COMMON.SYNC_COMPLETED'),
          detail: this.translate.instant('COMMON.SYNC_COMPLETED_MESSAGE') + ` (${result.success})`,
          life: 3000
        });
      } else if (result.conflicts > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Conflits de synchronisation',
          detail: `${result.conflicts} modification(s) en conflit avec le serveur. Les données serveur ont été conservées.`,
          life: 8000
        });
      } else if (result.success > 0 && result.failed > 0) {
        this.messageService.add({
          severity: 'warn',
          summary: this.translate.instant('COMMON.SYNC_PARTIAL'),
          detail: `${result.success} ${this.translate.instant('COMMON.SYNC_SUCCESS')}, ${result.failed} ${this.translate.instant('COMMON.SYNC_FAILED')}`,
          life: 5000
        });
      } else if (result.failed > 0) {
        // Retry une fois après 3 secondes
        console.warn('⚠️ Sync failed, retrying in 3s...');
        await new Promise(resolve => setTimeout(resolve, 3000));
        const retryResult = await this.offlineSyncService.syncAll();
        await this.offlineSyncService.reloadAllData();

        if (retryResult.failed > 0) {
          let detailMsg = this.translate.instant('COMMON.SYNC_ERROR_MESSAGE');
          if (retryResult.errors && retryResult.errors.length > 0) {
            detailMsg += '\n' + retryResult.errors.slice(0, 2).join('\n');
          }
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.SYNC_ERROR'),
            detail: detailMsg,
            life: 8000
          });
        } else {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SYNC_COMPLETED'),
            detail: 'Synchronisation réussie après nouvelle tentative',
            life: 3000
          });
        }
      }
    } catch (error) {
      console.error('❌ Auto-sync error:', error);
    } finally {
      this.isSyncing = false;
    }
  }
}
